import { createContext, useContext } from 'react';
import type { tConnectionState } from 'src/hooks/whatsapp/whatsappConnect.types';
import type { tWapiDevScenario } from 'src/hooks/whatsapp/useWhatsAppConnectState.hook';
import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';
import type { tConnectionMoment } from './WhatsAppConnectionWizard.functions';
import type {
  tWizardActivatingStage,
  tWizardAdvisorCtaClick,
  tWizardHistorySync,
} from './WhatsAppConnectionWizard.types';

/** The wizard's connection state and actions, which outlive the modal. */
export type tWhatsAppConnectionWizardContext = {
  state: tConnectionState;
  moment: tConnectionMoment;
  /** The step 1 answer; null until the person picks one. */
  selectedPath: tEmbeddedSignupPath | null;
  /** Picks an answer; null clears it, as a simulated scene with no answer does. */
  selectPath: (path: tEmbeddedSignupPath | null) => void;
  handleClose: () => void;
  handleConnect: () => void;
  handleRetry: () => void;
  handleEscalatedMount: () => void;
  handleAdvisorCtaClick: (click: tWizardAdvisorCtaClick) => void;
  backToConnect: () => void;
  /**
   * Reports the click and hands this attempt to the current modal, which
   * opens in the wizard's place while the modal stays open.
   */
  handleConnectWithoutGuide: () => void;
  /**
   * Whether the window filled the screen when Meta was last opened, so Meta
   * opened in another tab instead of beside the wizard.
   */
  isMetaInAnotherTab: boolean;
  /** Shows a simulated screen; does nothing while the screen simulator is disabled. */
  simulate: (scene: tWapiDevScenario) => void;
  /**
   * When the failed attempt on screen was restored from storage, the time it
   * happened. Absent for a failure of this session and once a new attempt
   * starts.
   */
  restoredFailureAt?: number;
  /**
   * Consecutive attempts that ended with Meta not activating the number, kept
   * in memory across modal closes. A failure the integration row restores
   * counts as one.
   */
  activationFailureCount: number;
  /**
   * Consecutive attempts whose exchange failed with no verdict, kept in
   * memory across modal closes. Any other end of an attempt resets it.
   */
  exchangeErrorCount: number;
  /** The connected number, readable; absent when the backend did not send it. */
  phoneNumber?: string;
  /** When Meta's activation decision was recorded; absent when unknown. */
  activationDecidedAt?: string;
  /**
   * Where a simulated activating screen starts; absent for a real exchange,
   * which starts from the first moment.
   */
  simulatedActivatingStage?: tWizardActivatingStage;
  /**
   * Whether the connected screen plays the brand moment: the workspace had
   * never connected when this attempt launched, or the screen is simulated.
   */
  playsBrandMoment: boolean;
  /**
   * The history sync a simulated connected screen shows; absent for a real
   * connection, whose sync comes from ProfilingProgress.
   */
  simulatedHistorySync?: tWizardHistorySync;
};

/**
 * Null outside `WhatsAppConnectionWizardProvider`, and inside it until the
 * wizard first opens in the current workspace and its state is published.
 */
export const WhatsAppConnectionWizardContext =
  createContext<tWhatsAppConnectionWizardContext | null>(null);

/**
 * Reads the wizard's connection state from `WhatsAppConnectionWizardProvider`.
 *
 * @returns {tWhatsAppConnectionWizardContext | null} The provider's value, or
 * null while there is no wizard state to read.
 */
export function useWhatsAppConnectionWizardContext(): tWhatsAppConnectionWizardContext | null {
  return useContext(WhatsAppConnectionWizardContext);
}
